import * as React from 'react';

import {WebVTT} from 'vtt.js';
import {useCSS} from '@pinecast/styles';

import {MOBILE} from './mobile';
import {Episode, Podcast} from './models';
import useAudioState from './useAudioState';
import {defineMessages, useIntl} from '@pinecast/i18n';

const messages = defineMessages({
  playing: {
    id: 'pl-spa.AudioCaptionHeader.playing',
    description: 'Message that cues are ready for play',
    defaultMessage: '[Playing]',
  },
});

type Cue = {
  start: number;
  end: number;
  text: string;
};

const AudioCaptionHeader = ({
  audio,
  podcast,
  episode,
}: {
  audio: HTMLAudioElement;
  podcast: Podcast;
  episode: Episode;
}) => {
  const css = useCSS();
  const intl = useIntl();
  const [cues, setCaptionData] = React.useState<Array<Cue> | null>(null);

  React.useEffect(() => {
    if (episode?.hasTranscript) {
      let controller: AbortController | null = new AbortController();
      let canceled = false;
      fetch(`/transcripts/vtt/${episode.id}`, {signal: controller.signal})
        .then(res => res.text())
        .then(text => {
          controller = null;
          return new Promise<Array<Cue>>((resolve, reject) => {
            const cues: Array<Cue> = [];
            const parser = new WebVTT.Parser(
              window,
              (WebVTT as any).StringDecoder(),
            );
            parser.oncue = (cue: VTTCue) => {
              cues.push({
                start: cue.startTime,
                end: cue.endTime,
                text: cue.text,
              });
            };
            parser.onflush = () => {
              resolve(cues);
            };
            parser.onparsingerror = reject;
            parser.parse(text);
            parser.flush();
          });
        })
        .then(cues => {
          if (canceled) return;
          setCaptionData(cues);
        });
      return () => {
        controller?.abort();
        canceled = true;
      };
    } else {
      setCaptionData(null);
      return undefined;
    }
  }, [episode]);

  const audioState = useAudioState(audio);

  const slid = episode.hasTranscript && audioState === 'playing' && cues;
  const [cue, setCue] = React.useState<Cue | null>(null);
  React.useEffect(() => {
    if (!cues) {
      setCue(null);
      return;
    }
    const onTimeUpdate = () => {
      const cue =
        cues!.find(
          cue => cue.start <= audio.currentTime && cue.end >= audio.currentTime,
        ) ?? null;
      if (!cue && cues!.length && cues![0].start > audio.currentTime) {
        setCue({
          start: 0,
          end: cues![0].start,
          text: intl.formatMessage(messages.playing),
        });
      }
      setCue(cue);
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      setCue(null);
    };
  }, [slid, audio, cues, intl]);

  return (
    <>
      <span
        className={css({
          fontSize: '13px',
          opacity: slid ? 0 : 1,
          textTransform: 'uppercase',
          transition: 'opacity 0.2s',
          [MOBILE]: {fontSize: '10px'},
        })}
      >
        {podcast.title}
      </span>
      <h1
        className={css({
          fontSize: '24px',
          fontWeight: 500,
          margin: 0,
          position: 'relative',
          top: slid ? '-20px' : 0,
          transition: 'top 0.2s',
          [MOBILE]: {fontSize: '20px', top: slid ? '-15px' : 0},
        })}
      >
        {episode.title}
      </h1>
      <div
        className={css({
          bottom: '-2px',
          left: 0,
          right: '4px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          opacity: slid ? 1 : 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
        })}
      >
        {cue?.text ?? <>&nbsp;</>}
      </div>
    </>
  );
};

export default AudioCaptionHeader;
